import { SetStateAction, useState, type FormEvent } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import PageHeader from "@/pages/_shared/PageHeader";
import { Link } from "react-router-dom";
import { FadeUp, Stagger, StaggerItem, SlideUpLine } from "@/pages/_shared/anim";
import ReCAPTCHA from "react-google-recaptcha";
import BrochurePdf from "@/assets/2026 UPlift catalog_Lite.pdf";

const categories = [
  {
    title: "Control & Automation",
    icon: "ri-cpu-line",
    items: [
      "Controllers", "Normal MR Controllers", "Slim MRL Controllers",
      "All kW Range Controllers", "Drives", "Door Drives",
      "HMI Panels", "COP & HOP", "Displays", "ARD Systems",
    ],
  },
  {
    title: "Electrical & Electronic",
    icon: "ri-flashlight-line",
    items: [
      "Batteries", "Resistors", "Transformers", "Power Supplies",
      "Encoders", "Test & Programming Tools",
    ],
  },
  {
    title: "Sensors & Switches",
    icon: "ri-sensor-line",
    items: [
      "Door Curtain Sensors", "Photoelectric Sensors", "Proximity Sensors",
      "Limit Switches", "Photo Switches", "Load Cells",
      "Magnetic Switches", "Micro Switches", "Key Switches",
    ],
  },
  {
    title: "Mechanical & Accessories",
    icon: "ri-settings-3-line",
    items: [
      "Traction Machines", "Buffers", "Cabin Cross-Flow Fans",
      "Door Accessories", "Rollers", "Door Headers", "Elevator Accessories",
    ],
  },
  {
    title: "Escalator Parts",
    icon: "ri-stairs-line",
    items: [
      "Drives", "Wheels", "Handrails", "Combs",
      "Rollers & Chains", "Motors", "Steps",
    ],
  },
];

const SITE_KEY = "6LcqH2EtAAAAAH4kWU7dB2gHeTrv41IKgLIzYP26";

const capabilities = [
  { icon: "ri-checkbox-circle-line", label: "Testing & Commissioning" },
  { icon: "ri-code-line", label: "Programming & Configuration" },
  { icon: "ri-survey-line", label: "Conditional Surveys" },
  { icon: "ri-lightbulb-line", label: "Technical Advice" },
  { icon: "ri-shield-check-line", label: "Safety Inspection" },
  { icon: "ri-file-list-3-line", label: "Reporting" },
  { icon: "ri-pencil-ruler-2-line", label: "Alternative Design Solutions" },
  { icon: "ri-tools-line", label: "Maintenance Support" },
];

export default function SparePartsPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
      if (!captchaToken) {
      setError("Please complete the CAPTCHA.");
      return;
  }
    const form = e.currentTarget;
    const data = new FormData(form);
    data.append("captcha", captchaToken);

    const description = (data.get("description") as string) || "";
    if (description.length > 500) {
      setError("Please keep your description under 500 characters.");
      return;
    }

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    try {
      setStatus("loading");
      const res = await fetch("/api/send-mail.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) throw new Error("submit failed");
      setStatus("success");
      form.reset();
      setCaptchaToken(null);
    } catch {
      setStatus("error");
      setError("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <div className="min-w-0 bg-crest-cream">
      <Navbar />
      <main>
        <PageHeader
          eyebrow="All-brand spare parts"
          title={
            <>
              Find the Part.
              <br />
              Solve the Problem.
              <br />
              <span className="text-white">Keep Moving.</span>
            </>
          }
          description="Uplift One provides an extensive range of elevator and escalator spare parts covering electrical, electronic, control, mechanical, sensor, safety, and specialized system components."
          crumbs={[
            { label: "Home", to: "/" },
            { label: "Spare Parts" },
          ]}
          meta={[
            { label: "Categories", value: "5" },
            { label: "Brand support", value: "Multi" },
            { label: "Part types", value: "50+" },
            { label: "Coverage", value: "UAE" },
          ]}
        />

        {/* Part Finder Form */}
        <section className="bg-crest-cream py-16 md:py-20">
          <div className="px-6 md:px-12">
            <div className="max-w-4xl mx-auto">
              <FadeUp className="text-center mb-10">
                <h2 className="font-display text-4xl md:text-5xl text-crest-deep leading-tight">
                  Looking for a Specific Part?
                </h2>
                <p className="text-crest-deep/70 text-lg mt-4 max-w-2xl mx-auto">
                  Send us the brand, model, part number, or a clear photo of the component. Our team can help review your requirement.
                </p>
              </FadeUp>

              <FadeUp delay={0.1}>
                <form
                  id="uplift-part-finder"
                  data-readdy-form
                  onSubmit={onSubmit}
                  className="relative bg-white rounded-xl p-6 md:p-10 border border-black/5"
                >
                   <input type="hidden" name="form_id" value="Home Page Form" />
                  {/* Honeypot: hidden from real users, bots tend to fill it in */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    className="absolute -left-[9999px] w-px h-px opacity-0"
                    aria-hidden="true"
                  />
                  {status === "success" ? (
                    <div className="flex flex-col items-center text-center py-10">
                      <span className="w-16 h-16 flex items-center justify-center rounded-full bg-crest-orange text-white mb-5">
                        <i className="ri-check-line text-3xl"></i>
                      </span>
                      <h3 className="font-display text-3xl text-crest-deep">
                        Part request received!
                      </h3>
                      <p className="text-crest-deep/70 mt-3 max-w-md">
                        Our team will review your requirement and get back to you with availability and pricing information.
                      </p>
                      <button
                        type="button"
                        onClick={() => setStatus("idle")}
                        className="mt-6 inline-flex items-center gap-2 bg-crest-orange text-white px-6 py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
                      >
                        Find another part
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Elevator / Escalator Brand *
                          </label>
                          <input
                            required
                            name="brand"
                            type="text"
                            placeholder="Brand name"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Part Name *
                          </label>
                          <input
                            required
                            name="part_name"
                            type="text"
                            placeholder="e.g., Door Drive Controller"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Part Number
                          </label>
                          <input
                            name="part_number"
                            type="text"
                            placeholder="Part number if known"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Your Email *
                          </label>
                          <input
                            required
                            name="email"
                            type="email"
                            placeholder="you@company.com"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Phone / WhatsApp
                          </label>
                          <input
                            name="phone"
                            type="tel"
                            placeholder="+971 50 123 4567"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                            Company
                          </label>
                          <input
                            name="company"
                            type="text"
                            placeholder="Company name"
                            className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                          Part Description
                        </label>
                        <textarea
                          name="description"
                          maxLength={500}
                          rows={4}
                          onChange={(e) => setCharCount(e.target.value.length)}
                          placeholder="Describe the component, its function, or any additional details that may help…"
                          className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40 resize-none"
                        />
                        <div className="text-xs text-crest-deep/50 mt-1 flex justify-between">
                          <span>Max 500 characters.</span>
                          <span>{charCount}/500</span>
                        </div>
                      </div>

                      {error && (
                        <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                          {error}
                        </div>
                      )}

                    <div className="mt-6">
                      <ReCAPTCHA
                          sitekey={SITE_KEY}
                          onChange={(token: SetStateAction<string>) => {
                          setCaptchaToken(token);
                          if (error === "Please complete the CAPTCHA.") {
                            setError("");
                           }
                             }}
                              onExpired={() => {
                                setCaptchaToken(null);
                           }}
                           onErrored={() => {
                              setCaptchaToken(null);
                          }}
                        />
                  </div>
                 
                  <div className="mt-6 flex flex-wrap gap-4">
                      <button
                        type="submit"
                        disabled={status === "loading"|| !captchaToken}
                        className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-crest-orangeLight transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                      >
                        {status === "loading" ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Sending…
                          </>
                        ) : (
                          <>
                            FIND MY PART
                            <i className="ri-arrow-right-up-line"></i>
                          </>
                        )}
                      </button>
                          <a
                            href={BrochurePdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-crest-orangeLight transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                           >
                            <i className="ri-file-pdf-line"></i>
                            VIEW Catelog
                          </a>
                      </div>
                    </>
                  )}
                </form>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* Product Categories */}
        <section className="bg-crest-deep py-20 md:py-24" data-product-shop>
          <div className="px-6 md:px-12">
            <FadeUp className="max-w-2xl mb-12">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-orange uppercase">
                <span className="w-6 h-px bg-crest-orange/60" /> Product categories
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-white leading-tight mt-5">
                Comprehensive range of spare parts.
              </h2>
            </FadeUp>

            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" stagger={0.08}>
              {categories.map((c) => (
                <StaggerItem
                  key={c.title}
                  className="bg-crest-bg/30 border border-white/10 rounded-lg p-6"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-orange text-white">
                      <i className={`${c.icon} text-lg`}></i>
                    </span>
                    <h3 className="font-display text-xl text-white">{c.title}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.items.map((item) => (
                      <span
                        key={item}
                        className="text-xs px-3 py-1.5 rounded-full bg-white/10 text-white/80 border border-white/10 whitespace-nowrap"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* Additional Capabilities */}
        <section className="bg-white py-20 md:py-24">
          <div className="px-6 md:px-12">
            <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <h2 className="font-display text-4xl md:text-5xl text-crest-deep leading-tight max-w-xl">
                Plus the Technical Support That Keeps Systems Moving.
              </h2>
              <p className="text-crest-deep/70 max-w-md text-base leading-relaxed">
                Beyond spare parts, our technical capabilities help keep your elevator and escalator systems operating reliably.
              </p>
            </FadeUp>
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.06}>
              {capabilities.map((a) => (
                <StaggerItem
                  key={a.label}
                  className="flex items-center gap-4 p-5 bg-crest-cream rounded-lg border border-black/5"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-crest-deep text-crest-orange shrink-0">
                    <i className={`${a.icon} text-xl`}></i>
                  </span>
                  <span className="text-crest-deep font-medium text-sm">
                    {a.label}
                  </span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-crest-cream py-16 md:py-20 border-t border-black/5">
          <FadeUp>
            <div className="px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h3 className="font-display text-3xl md:text-4xl text-crest-deep leading-tight">
                  Can't find what you're looking for?
                </h3>
                <p className="text-crest-deep/70 mt-3 max-w-2xl">
                  Send us a photo or details of the component. Even if it's not listed, our team can help source or identify alternatives.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-crest-orangeLight transition-colors"
              >
                Get technical help
                <i className="ri-arrow-right-up-line"></i>
              </Link>
            </div>
          </FadeUp>
        </section>
      </main>
      <Footer />
    </div>
  );
}